<script setup lang="ts">

import {route} from "ziggy-js";
import {User} from "@/types";
import {computed, onMounted, ref, useTemplateRef} from "vue";
import {useUserAvatar} from "@/composables/avatar";
import {router} from "@inertiajs/vue3";
import * as Sentry from "@sentry/vue";

interface Props {
  modelValue: boolean
  user: User
}
const props = defineProps<Props>()
const emit = defineEmits(['update:modelValue'])
const value = computed({
    get() {
        return props.modelValue
    },
    set(value) {
        emit('update:modelValue', value)
    }
})

const feedback = ref<any>(null)

const goToProfile = () => {
    router.visit(route('users.show', {user: props.user.name}), {
        onSuccess: () => {
            value.value = false
        }
    })
}

const goToSettings = () => {
    router.visit(route('settings.profile.show'), {
        onSuccess: () => {
            value.value = false
        }
    })
}

const logout = () => {
    router.post(route('logout'))
}

onMounted(() => {
    feedback.value = Sentry.getFeedback();
});

const openFeedbackForm = async () => {
    if (!feedback.value) return;
    const form = await feedback.value.createForm();
    form.appendToDom();
    form.open();
}
</script>

<template>
    <v-navigation-drawer
        v-model="value"
        :temporary="true"
        location="end"
        app
        scrim="grey"
        width="320"
        class="elevation-5 rounded-ts-xl rounded-bs-xl"
    >
        <v-list>
            <v-list-item
                density="comfortable"
                class="py-3"
                :slim="true"
            >
                <template #prepend>
                    <v-avatar class="border-sm">
                        <v-img
                            :src="useUserAvatar(props.user.name)"
                            contain
                        />
                    </v-avatar>
                </template>
                <v-list-item-title class="font-weight-bold text-body-2 ml-1">
                    {{ props.user.name }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-black ml-1">
                    {{ props.user.full_name || props.user.email }}
                </v-list-item-subtitle>
                <template #append>
                    <v-btn
                        variant="text"
                        icon="mdi-close"
                        size="small"
                        rounded="lg"
                        @click="value = false"
                    />
                </template>
            </v-list-item>
            <v-divider class="my-2" />
            <v-list-item
                density="compact"
                :link="true"
                prepend-icon="mdi-account-outline"
                class="ma-2 cursor-pointer"
                rounded="lg"
                :slim="true"
                @click="goToProfile"
            >
                <v-list-item-title class="text-body-2 font-weight-regular">
                    Your profile
                </v-list-item-title>
            </v-list-item>
            <v-divider class="my-2" />
            <v-list-item
                density="compact"
                :link="true"
                prepend-icon="mdi-cog-outline"
                class="ma-2"
                rounded="lg"
                :slim="true"
                @click="goToSettings"
            >
                <v-list-item-title class="text-body-2 font-weight-regular">
                    Settings
                </v-list-item-title>
            </v-list-item>
            <v-divider class="my-2" />
            <v-list-item
                density="compact"
                :link="true"
                prepend-icon="mdi-book-open-page-variant-outline"
                class="ma-2"
                rounded="lg"
                :slim="true"
                href="https://clinbiokb.sib.swiss/s/sendcrypt"
                target="_blank"
            >
                <v-list-item-title class="text-body-2 font-weight-regular">
                    SendCrypt Docs
                </v-list-item-title>
            </v-list-item>
            <v-list-item
                density="compact"
                :link="true"
                prepend-icon="mdi-account-group-outline"
                class="ma-2"
                rounded="lg"
                :slim="true"
                href="mailto:sendcrypt-support@sib.swiss?subject=SendCrypt%20Support"
            >
                <v-list-item-title class="text-body-2 font-weight-regular">
                    SendCrypt Support
                </v-list-item-title>
            </v-list-item>
            <v-divider class="my-2" />
            <v-list-item
                :link="true"
                prepend-icon="mdi-bullhorn-outline"
                class="ma-2"
                rounded="lg"
                :slim="true"
                @click="openFeedbackForm"
            >
                <v-list-item-title class="text-body-2">
                    Report a bug
                </v-list-item-title>
            </v-list-item>
            <v-divider class="my-2" />
            <v-list-item
                :link="true"
                class="ma-2"
                rounded="lg"
                :slim="true"
                @click="logout"
            >
                <v-list-item-title class="text-body-2">
                    Sign out
                </v-list-item-title>
            </v-list-item>
        </v-list>
    </v-navigation-drawer>
</template>

<style scoped>
.v-navigation-drawer__scrim {
    opacity: 0.1;
}
</style>
